import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { InputMaskModule } from 'primeng/inputmask';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { UserProfile } from '../../../shared/models/auth.model';
import { LettersService } from '../../../shared/services/letters.service';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-letters-reply',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    DatePickerModule,
    InputMaskModule,
    MessageModule,
    ToastModule,
    SelectModule,
  ],
  providers: [MessageService],
  templateUrl: './letters-reply.component.html',
  styleUrl: './letters-reply.component.scss',
})
export class LettersReplyComponent implements OnInit {

  // Form
  replyForm!: FormGroup;

  // User profile data
  userProfile: UserProfile = {
    id: '',
    name: '',
    email: '',
    role: '',
    avatar: 'assets/images/logo.png',
  };

  // Document info
  documentName: string = '';
  letterId: string = '';
  isLoading: boolean = false;

  // Reply-to users list
  replyToUsers: { id: string; fullName: string }[] = [];
  isLoadingUsers: boolean = false;

  constructor(
    private fb: FormBuilder, 
    private route: ActivatedRoute, 
    private router: Router,
    private lettersService: LettersService,
    private authService: AuthService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadDocumentInfo();
    this.loadReplyToUsers();
  }

  private loadDocumentInfo(): void {
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.letterId = params['id'];
        // Load document details to get the document name
        this.lettersService.getLetterById(this.letterId).subscribe({
          next: (response) => {
            this.documentName = response.documentName || 'Document';
          },
          error: (error) => {
            this.documentName = 'Document'; // Fallback name
            this.messageService.add({
              severity: 'warn',
              summary: 'Warning',
              detail: 'Could not load document name.',
              life: 3000
            });
          }
        });
      } else {
        // If no ID is provided, redirect back to letters list
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No letter ID provided. Redirecting to letters list.',
          life: 3000
        });
        setTimeout(() => {
          this.router.navigate(['/letters']);
        }, 3000);
      }
    });
  }

  private initializeForm(): void {
    this.replyForm = this.fb.group({
      replyDate: [new Date(), Validators.required],
      replyTime: ['14:00', Validators.required],
      replyTo: ['', Validators.required],
      replySubject: ['', Validators.required],
    });
  }

  private loadReplyToUsers(): void {
    this.isLoadingUsers = true;
    this.lettersService.getReplyToUsersList().subscribe({
      next: (users) => {
        // Get the logged-in user
        const currentUser = this.authService.getCurrentUser();
        
        // Filter out the logged-in user from the list
        if (currentUser && currentUser.name) {
          this.replyToUsers = users.filter(
            (user) => user.fullName !== currentUser.name
          );
        } else {
          this.replyToUsers = users;
        }
        
        this.isLoadingUsers = false;
      },
      error: (error) => {
        this.isLoadingUsers = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load users list. Please try again.',
          life: 3000
        });
      }
    });
  }

  onSubmit(): void {
    if (this.replyForm.valid && this.letterId) {
      this.isLoading = true;
      
      const formValue = this.replyForm.value;
      
      // Prepare the payload according to the API specification
      const replyData = {
        letterId: this.letterId,
        replySubject: formValue.replySubject,
        replyDate: formValue.replyDate.toISOString(), // Convert Date to ISO string
        replyTime: formValue.replyTime,
        replyTo: formValue.replyTo, // This will be the user id
        sender: '' // Empty string as sender field is removed
      };
      
      // Call the API
      this.lettersService.replyLetter(replyData).subscribe({
        next: (response) => {
          this.isLoading = false;
          
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Reply submitted successfully!',
            life: 5000
          });
          
          // Navigate back to letters list after a short delay
          setTimeout(() => {
            this.router.navigate(['/letters']);
          }, 2000);
        },
        error: (error) => {
          this.isLoading = false;
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to submit reply. Please try again.',
            life: 5000
          });
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    // Navigate back to letters list
    this.router.navigate(['/letters']);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.replyForm.controls).forEach(key => {
      const control = this.replyForm.get(key);
      control?.markAsTouched();
    });
  }


  isInvalid(controlName: string): boolean {
    const control = this.replyForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }
}
